export interface DeviceInput {
  readonly deviceId: string;
  readonly deviceName?: string;
  readonly deviceType: 'web' | 'ios' | 'android' | 'unknown';
}

const STORAGE_KEYS = {
  deviceId: 'device_id',
  deviceType: 'device_type',
  deviceName: 'device_name',
} as const;

function safeGet(storage: Storage | undefined, key: string): string | null {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function safeSet(storage: Storage | undefined, key: string, value: string): void {
  try {
    storage?.setItem(key, value);
  } catch {
    // Storage unavailable (private mode, quota, busted polyfill) — silently degrade.
  }
}

function getUserAgent(): string {
  if (typeof navigator !== 'undefined') return navigator.userAgent;
  return '';
}

function randomUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // RFC4122-ish fallback for environments without crypto.randomUUID.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function getDeviceId(): string {
  if (typeof window === 'undefined') return '';
  const ls = window.localStorage as Storage | undefined;
  let deviceId = safeGet(ls, STORAGE_KEYS.deviceId);
  if (!deviceId) {
    deviceId = randomUUID();
    safeSet(ls, STORAGE_KEYS.deviceId, deviceId);
  }
  return deviceId;
}

function getDeviceType(): DeviceInput['deviceType'] {
  if (typeof window === 'undefined') return 'unknown';
  const ss = window.sessionStorage as Storage | undefined;
  const cached = safeGet(ss, STORAGE_KEYS.deviceType);
  if (cached) return cached as DeviceInput['deviceType'];

  const ua = getUserAgent().toLowerCase();
  let deviceType: DeviceInput['deviceType'] = 'web';
  if (/iphone|ipad|ipod/.test(ua)) deviceType = 'ios';
  else if (/android/.test(ua)) deviceType = 'android';

  safeSet(ss, STORAGE_KEYS.deviceType, deviceType);
  return deviceType;
}

function getDeviceName(): string {
  if (typeof window === 'undefined') return 'Unknown Browser';
  const ss = window.sessionStorage as Storage | undefined;
  const cached = safeGet(ss, STORAGE_KEYS.deviceName);
  if (cached) return cached;

  const ua = getUserAgent();
  let deviceName = 'Unknown Browser';
  if (ua.includes('Edg')) deviceName = 'Edge';
  else if (ua.includes('Chrome')) deviceName = 'Chrome';
  else if (ua.includes('Firefox')) deviceName = 'Firefox';
  else if (ua.includes('Safari')) deviceName = 'Safari';
  else if (ua.includes('Opera')) deviceName = 'Opera';

  safeSet(ss, STORAGE_KEYS.deviceName, deviceName);
  return deviceName;
}

export function getDeviceInput(): DeviceInput {
  return {
    deviceId: getDeviceId(),
    deviceName: getDeviceName(),
    deviceType: getDeviceType(),
  };
}
